import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { buildCumulativeData, buildCostControlDataFromRelease, computeHistoricalSnapshot, resolveCurrentForecastState, applyLiveForecastToCumulative } from '../lib/buildCostControlData'
import { buildMonthlyActualCostData } from '../lib/monthlyActualCost'
import { assessDataConfidence } from '../lib/executiveDataConfidence'
import { compareSnapshots, narrateMonetaryTrend, narrateCpiTrend } from '../lib/trendCompare'
import { C } from '../lib/designTokens'

// Previous reporting period, expressed as a forecast_history-style date
// string ('YYYY-MM-01'), for the Trend Indicators snapshot comparison.
function previousForecastDate() {
  const now = new Date()
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const yyyy = prevMonth.getFullYear()
  const mm = String(prevMonth.getMonth() + 1).padStart(2, '0')
  return `${yyyy}-${mm}-01`
}

const fmt  = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
const fmtM = (n) => { const m = (n||0)/1e6; return m >= 1 ? m.toFixed(1)+'M' : fmt(n) }
const pct  = (a, b) => b ? ((a/b)*100).toFixed(1)+'%' : '—'
const cpi  = (ev, ac) => ac ? (ev/ac).toFixed(3) : '—'
const today = () => new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })
const monthYear = () => new Date().toLocaleDateString('en-GB', { month:'long', year:'numeric' })

// Same period list as ExecutiveReportConfig.jsx (local-date construction,
// no toISOString/UTC conversion — see that file's monthOptions() for the
// timezone bug this avoids).
function configPeriodOptions() {
  const out = []
  const d = new Date()
  d.setDate(1)
  for (let i = 0; i < 12; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1)
    const yyyy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    out.push({ value: `${yyyy}-${mm}-01`, label: dt.toLocaleDateString('en-GB', { month:'long', year:'numeric' }) })
  }
  return out
}

/** Report logo: shows the organization's real logo when available, falling
 *  back to the CostSpace mark (brand blue, matching designTokens.js C.blue). */
function ReportLogo({ org, size = 44 }) {
  if (org?.logo_url) {
    return (
      <div style={{width:size,height:size,borderRadius:size>32?12:8,overflow:'hidden',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <img src={org.logo_url} alt={org.name||'Logo'} style={{width:'100%',height:'100%',objectFit:'contain'}}/>
      </div>
    )
  }
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{flexShrink:0}}>
      <rect width="100" height="100" rx="24" fill="#1B3A6B"/>
      <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
      <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
    </svg>
  )
}

/** Presentation-only trend badge — renders a {buildTrend()} result from
 *  trendCompare.js. Does no calculation itself, only formatting/layout. */
function TrendBadge({ trend, formatDiff = fmtM }) {
  if (!trend || trend.previous === 0) return null // no prior-period data to compare against
  const sign = trend.diff > 0 ? '+' : trend.diff < 0 ? '-' : ''
  const pctLabel = trend.pct !== null ? ` (${sign}${Math.abs(trend.pct).toFixed(1)}%)` : ''
  return (
    <span style={{fontSize:10,fontWeight:700,color:trend.color,marginLeft:6,whiteSpace:'nowrap'}}>
      {trend.arrow} {sign}{formatDiff(Math.abs(trend.diff))}{pctLabel}
    </span>
  )
}

export default function ExecutiveReport({ projectId, onClose, onOpenConfig }) {
  const [project,   setProject]   = useState(null)
  const [org,       setOrg]       = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [data,      setData]      = useState(null)
  // Batch B: manually-entered report content, fetched and stored completely
  // separately from `data` (financial calculations). Never merged into the
  // `data` object and never used in any financial computation.
  const [config,    setConfig]    = useState(null)
  // Batch B fix: which reporting period's config to show. Defaults to the
  // current calendar month, but the user can pick any of the last 12 months
  // — same period list as ExecutiveReportConfig.jsx, so both pages always
  // agree on what a "period" means.
  const [configPeriod, setConfigPeriod] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })
  // reportContext — same pattern as CostControl.jsx's own source-of-truth
  // selector: { source: 'live', releaseId: null } for Current (Live), or
  // { source: 'release', releaseId } for a Project Forecast Release.
  // Current reads buildCumulativeData(); Release reads
  // buildCostControlDataFromRelease() — no separate reporting pipeline.
  const [reportContext, setReportContext] = useState({ source: 'live', releaseId: null })
  const [availableReleases, setAvailableReleases] = useState([])
  const printRef = useRef()

  useEffect(() => { if (projectId) loadAvailableReleases(projectId) }, [projectId]) // eslint-disable-line
  useEffect(() => { if (projectId) fetchAll(projectId) }, [projectId, reportContext]) // eslint-disable-line

  // Identical query/shape to CostControl.jsx's loadAvailableReleases() —
  // same view (project_forecast_release_summary), same id-mapping.
  async function loadAvailableReleases(pid) {
    const { data } = await supabase
      .from('project_forecast_release_summary')
      .select('release_id, project_id, forecast_date, release_number, direct_snapshot_id, direct_revision_number, indirect_snapshot_id, indirect_revision_number, notes, created_at, is_current')
      .eq('project_id', pid)
      .order('forecast_date', { ascending: false })
      .order('release_number', { ascending: false })
    const releases = (data || []).map(r => ({ ...r, id: r.release_id }))
    setAvailableReleases(releases)
  }

  async function fetchAll(pid) {
    setLoading(true)

    // ── Shared sources — this report performs NO financial calculations of
    // its own. Every BAC/Commitment/Actual/Accrual/EAC/VAC/EV/CPI/Cash/Gross
    // Profit figure comes from buildCumulativeData() (Current/Live) or
    // buildCostControlDataFromRelease() (Project Forecast Release) — the
    // exact same two build functions CostControl.jsx itself uses, via the
    // same reportContext.source switch. No separate reporting pipeline.
    //
    // "This Period" (Movement, from buildMonthlyActualCostData()) and Trend
    // Indicators (previousSnapshot, from computeHistoricalSnapshot()) are
    // BOTH Live-only concepts — Movement means "change since the last two
    // periods with live transaction data," and Trend means "compared to
    // last month's live forecast." Neither has a well-defined meaning for
    // a frozen historical Release (explicitly decided: hide, don't attempt
    // a Release-vs-previous-Release comparison), so both are skipped
    // entirely when reportContext.source === 'release', rather than
    // computed against Live data while every other figure on screen is
    // frozen Release data — mixing the two would silently blend two
    // different points in time into one report.
    const isRelease = reportContext.source === 'release'

    let cumulative, previousSnapshot, monthly
    if (isRelease) {
      const release = availableReleases.find(r => r.id === reportContext.releaseId)
        || (reportContext.releaseId ? (await supabase.from('project_forecast_releases').select('*').eq('id', reportContext.releaseId).single()).data : null)
      // buildMonthlyActualCostData() is fetched even for Release — it reads
      // live transaction tables (ipc_certificates, store_issues, etc.),
      // entirely independent of forecast_history/the snapshot, so there is
      // no reason to withhold it. Only Trend Indicators (a comparison
      // between two Snapshot points, which Release doesn't have a defined
      // "previous" for) remain Live-only.
      ;[cumulative, monthly] = await Promise.all([
        release ? buildCostControlDataFromRelease(release) : Promise.resolve({ directRows: [], indirectRows: [], directTotals: {}, indirectTotals: {}, grandTotals: {}, meta: { contractValue: 0, cashInTotal: 0, cashOutTotal: 0, netCash: 0, grossProfit: 0, budgetProfit: 0 } }),
        buildMonthlyActualCostData(pid),
      ])
      previousSnapshot = { grandTotals: {} } // Trend Indicators hidden for Release — see note above
    } else {
      ;[cumulative, previousSnapshot, monthly] = await Promise.all([
        buildCumulativeData(pid),
        computeHistoricalSnapshot(pid, previousForecastDate()),
        buildMonthlyActualCostData(pid),
      ])
      // #8: Current (Live) must describe the SAME live state as Cost Control (Live): the active Draft forecast applied on top of
      // buildCumulativeData(). It is applied by the ONE shared function (not re-implemented here), so EAC, the row-level and total
      // variances, CPI and gross profit all agree with Cost Control. Release (above) is untouched and keeps its frozen snapshot path.
      const [resolvedDirect, resolvedIndirect] = await Promise.all([
        resolveCurrentForecastState(pid, 'direct'),
        resolveCurrentForecastState(pid, 'indirect'),
      ])
      cumulative = applyLiveForecastToCumulative(cumulative, resolvedDirect, resolvedIndirect)
    }

    const [
      { data: proj },
      { data: poRows },
      { data: subRows },
      { data: riskRows },
      { data: periodConfigRow },
    ] = await Promise.all([
      supabase.from('projects').select('*, organizations(name, logo_url)').eq('id', pid).single(),
      // Procurement snapshot counts — simple row counts, not financial
      // aggregations, so they stay local rather than living in the shared
      // financial engine (which exists to centralize money calculations,
      // not every count/lookup a report might want to show).
      supabase.from('purchase_orders').select('id', { count: 'exact', head: true }).eq('project_id', pid),
      supabase.from('subcontracts').select('id', { count: 'exact', head: true }).eq('project_id', pid).eq('status', 'approved'),
      supabase.from('risk_register').select('*').eq('project_id', pid).eq('status', 'open').order('financial_impact', { ascending: false, nullsFirst: false }).order('risk_score', { ascending: false }),
      // Actual Reporting Period (From/To Month) — read directly here
      // (independent of the separate config useEffect below, which is
      // keyed off the narrative configPeriod selector) so it's guaranteed
      // available at the exact moment thisPeriod is computed for a
      // Release, with no ordering dependency between the two.
      isRelease
        ? supabase.from('executive_report_configs').select('actual_period_from, actual_period_to').eq('project_id', pid).eq('reporting_period', configPeriod).maybeSingle()
        : Promise.resolve({ data: null }),
    ])

    setProject(proj)
    setOrg(proj?.organizations || null)

    const g = cumulative.grandTotals
    const meta = cumulative.meta

    // "This Period" for a Release — per this conversation's finalized
    // approach: NOT frozen into the snapshot (This Period was confirmed
    // Out of Scope for the Cost Forecast's frozen financial state — see
    // the earlier parity audit). Instead, for a Release, This Period is a
    // user-configured cumulative sum: "Actual cost between the configured
    // From/To months" — reusing buildMonthlyActualCostData()'s own
    // per-Cost-Code periodTotals (already computed, already correct),
    // summed only across the configured range. No new calculation engine:
    // this is a plain sum of pre-computed monthly figures the same module
    // already produces for Current's Movement figure.
    function sumPeriodRange(periodTotals, fromMonth, toMonth) {
      if (!periodTotals || !fromMonth || !toMonth) return 0
      return Object.entries(periodTotals)
        .filter(([period]) => period >= fromMonth.slice(0, 7) && period <= toMonth.slice(0, 7))
        .reduce((s, [, v]) => s + (v || 0), 0)
    }
    const actualPeriodFrom = periodConfigRow?.actual_period_from || null
    const actualPeriodTo = periodConfigRow?.actual_period_to || null
    const hasConfiguredActualPeriod = isRelease && actualPeriodFrom && actualPeriodTo

    // Package (Cost Code) breakdown for the Page 3 Cost Summary table.
    // Budget/Actual/EAC/VAC/CPI all come straight from cumulative.directRows /
    // cumulative.indirectRows (the same rows Cost Control renders) — this
    // report does not recompute any of them. "This Period" (Movement) for
    // each Cost Code comes from the separate monthly.byCostCode lookup,
    // matched by the Cost Code's text code (see monthlyActualCost.js's doc
    // comment on why byCostCode is keyed by code, not cost_code_id).
    const packages = [
      ...cumulative.directRows.map(r => ({ ...r, type: 'direct' })),
      ...cumulative.indirectRows.map(r => ({ ...r, type: 'indirect' })),
    ].map(r => ({
      name: r.description ? `${r.code} — ${r.description}` : r.code,
      code: r.code,
      type: r.type,
      budget: r.bac,
      actual: r.totalActualCost,
      thisPeriod: hasConfiguredActualPeriod
        ? sumPeriodRange(monthly.byCostCode[r.code]?.periodTotals, actualPeriodFrom, actualPeriodTo)
        : (isRelease ? 0 : (monthly.byCostCode[r.code]?.movement || 0)),
      eac: r.eac,
      vac: r.varianceCompletion,
      cpi: r.cpi,
    })).sort((a, b) => b.budget - a.budget)

    // "Areas of Concern" is the single source for financially-negative
    // packages (vac < 0) — a prior version of this report also had a
    // separate "Top Cost Overruns" section using the exact same filter and
    // sort, differing only in how many items were sliced off. That was a
    // duplicate presentation of the same business concept, not a distinct
    // one, so it's been removed; this list now serves both purposes at a
    // size (10) suited to a dedicated page.
    const concerns = packages.filter(p => p.vac < 0).sort((a, b) => a.vac - b.vac).slice(0, 10)
    // Top Cost Savings — genuinely distinct: the opposite VAC direction
    // (vac > 0), not a duplicate of Areas of Concern.
    const topSavings = packages.filter(p => p.vac > 0).sort((a, b) => b.vac - a.vac).slice(0, 5)

    // Report Confidence — validates the reliability/completeness of the
    // data already computed above. Uses only existing outputs (packages,
    // meta.grossProfit, meta.contractValue, the raw riskRows already
    // fetched) — no new query, no new financial calculation. See
    // executiveDataConfidence.js's module doc comment for the full
    // architecture rationale.
    const dataConfidence = assessDataConfidence(packages, meta.grossProfit, meta.contractValue, riskRows)

    // Trend Indicators — pure presentation comparison of two already-computed
    // grandTotals objects (current vs previous snapshot). compareSnapshots()/
    // buildTrend() (trendCompare.js) do no financial math beyond diff/pct/
    // direction; every underlying number still comes from buildCumulativeData().
    // Only EAC-derived fields are compared — see the limitation note above.
    // Explicitly skipped for Release (not merely passed an empty
    // previousSnapshot and left to compareSnapshots()'s internal handling)
    // — Trend Indicators have no defined meaning for a frozen historical
    // Release, per this conversation's explicit decision to hide them
    // rather than guess at compareSnapshots()'s behavior with empty inputs.
    const trends = isRelease ? {} : compareSnapshots(g, previousSnapshot.grandTotals || {}, {
      eac:         { path: 'eac',                 higherIsBetter: false }, // lower forecast = good news
      vac:         { path: 'varianceCompletion',  higherIsBetter: true  }, // higher VAC = good news
      cpi:         { path: 'cpi',                 higherIsBetter: true  },
      grossProfit: { path: 'grossProfit',         higherIsBetter: true  },
    })

    setData({
      // Grand Total KPIs — every value read directly from the shared engine,
      // nothing recalculated here.
      isRelease,
      hasConfiguredActualPeriod,
      actualPeriodFrom,
      actualPeriodTo,
      releaseInfo: isRelease ? availableReleases.find(r => r.id === reportContext.releaseId) : null,
      bac: g.bac,
      directBudget: cumulative.directTotals.bac,
      indirectBudget: cumulative.indirectTotals.bac,
      actualCost: g.totalActualCost,
      thisPeriod: hasConfiguredActualPeriod
        ? packages.reduce((s, pk) => s + (pk.thisPeriod || 0), 0)
        : (isRelease ? 0 : monthly.grandMovement),
      eac: g.eac,
      vac: g.varianceCompletion,
      contractValue: meta.contractValue,
      grossProfit: meta.grossProfit,
      budgetProfit: meta.budgetProfit,
      commitment: g.commitment,
      ev: g.earnedValue,
      evPct: g.bac ? (g.totalActualCost / g.bac) * 100 : 0,
      cpiVal: g.cpi,
      cashIn: meta.cashInTotal,
      cashOut: meta.cashOutTotal,
      netCash: meta.netCash,
      packages, concerns, topSavings, dataConfidence, trends,
      subCount: subRows?.length ?? 0,
      poCount: poRows?.length ?? 0,
      risks: (riskRows || []).slice(0, 5),
    })

    setLoading(false)
  }

  // Batch B fix: config is fetched independently of fetchAll(), keyed off
  // configPeriod, so changing the selected period re-fetches only the
  // manual content — never re-runs the financial queries above. Uses the
  // same local-date (no UTC conversion) period-key convention as
  // ExecutiveReportConfig.jsx, so both pages agree on which period's config
  // row to read/write.
  useEffect(() => {
    if (!projectId) return
    ;(async () => {
      const { data: configRow } = await supabase
        .from('executive_report_configs')
        .select('*')
        .eq('project_id', projectId)
        .eq('reporting_period', configPeriod)
        .maybeSingle()
      setConfig(configRow || null)
    })()
  }, [projectId, configPeriod])

  function handlePrint() { window.print() }

  // Batch B: Executive Brief now renders as the LAST page of the report
  // (after Cost Summary), not right after the cover. KPIs is always Page 2
  // and Cost Table is always Page 3 — neither shifts anymore, since nothing
  // before them is conditional. Brief's own page number is whatever page
  // number follows Cost Table, computed only when it actually renders.
  // Page order: Cover(1) -> Executive Brief(2, conditional on having content)
  // -> KPI Dashboard(3 or 2) -> Executive Decisions(4 or 3) -> Appendix(5 or 4).
  // Brief's visibility depends on whether any of its five fields are set
  // (same "skip entirely if empty" rule as before, just moved earlier in
  // the page order) — so every later page number shifts down by one
  // whenever Brief has no content, exactly as it used to shift when Brief
  // was the last page.
  const briefSections = [
    { label: 'Executive Summary',   value: config?.executive_summary },
    { label: 'Project Highlights',  value: config?.project_highlights },
    { label: 'Key Challenges',      value: config?.key_challenges },
    { label: 'Recommendations',     value: config?.recommendations },
    { label: 'Additional Notes',    value: config?.additional_notes },
  ].filter(s => s.value && String(s.value).trim().length > 0)
  const hasBrief = briefSections.length > 0

  const briefPageNum = 2
  const kpiPageNum = hasBrief ? 3 : 2
  const decisionsPageNum = kpiPageNum + 1
  const appendixPageNum = decisionsPageNum + 1

  if (loading) return (
    <div style={{position:'fixed',inset:0,background:'#F3F4F6',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:48,height:48,border:'4px solid #E5E7EB',borderTop:'4px solid #1B3A6B',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}}/>
        <p style={{color:'#6B7280',fontFamily:'inherit'}}>Generating Report...</p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  if (!data) return null
  const d = data
  const p = project

  const VAC_COLOR = (v) => v >= 0 ? '#1A7A4A' : '#DC2626'
  const VAC_BG    = (v) => v >= 0 ? '#F0FDF4' : '#FEF2F2'

  return (
    <div className="exec-report-modal-root" style={{position:'fixed',inset:0,background:'#CBD5E0',overflowY:'auto',zIndex:9999,fontFamily:"'Inter','IBM Plex Sans',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        @page { size: A4 portrait; margin: 10mm 0 }
        @media print {
          /* Force background colors/gradients to print exactly as shown on
             screen, regardless of the browser's print dialog "background
             graphics" setting. Without this, headers, KPI cards, and badges
             print as plain white in most browsers by default. */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }

          body * { visibility: hidden }
          #exec-report, #exec-report * { visibility: visible }
          #exec-report { position: absolute; left: 0; top: 0; width: 100% }
          .no-print { display: none !important }

          /* ── Pagination fix ──────────────────────────────────────────
             Root cause of the large blank areas seen under the Cover and
             KPI pages: @page forces every printed sheet to be a full A4
             page (297mm tall), but each .page <div> only grows to its
             actual content height — a few hundred px for the Cover, more
             for KPIs, much more for the Cost Summary table. Whichever
             .page starts a given printed sheet (the first .page always
             starts sheet 1; anything after a page-break-before starts its
             own sheet) leaves the REST of that sheet unfilled once its
             own content ends — and since .exec-report-modal-root's own
             background (#CBD5E0, or plain white) sits behind everything,
             that unfilled remainder of the sheet shows through as a
             visibly blank area, exactly as reported.
             Fix: each .page is given a print-only min-height matching
             A4's printable height (297mm sheet − 20mm total margin from
             the @page rule above = 277mm), so its own background/border
             extends to fill the entire printed sheet regardless of how
             short its actual content is — the Cover's dark background,
             for instance, now reaches the bottom of its sheet instead of
             stopping partway and leaving a mismatched blank gap below it.
             This does NOT reintroduce the original bug (a fixed px
             minHeight fighting page-break-after:always): there is no
             forced break here, only a print-only min-height, and any
             page whose content genuinely exceeds one sheet (the Cost
             Summary table) simply flows onto additional sheets as normal
             — min-height never truncates or clips taller content. */
          .page { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; margin-bottom: 6mm; min-height: 277mm; }
          .page-break-before { page-break-before: always; break-before: page; }
          .page:last-child { margin-bottom: 0; }

          /* Cost Summary table: repeat header on every printed page, and
             never split a single row across a page boundary — without this,
             a project with many cost codes/packages loses its column
             headers on every page after the first when the table spans
             more than one printed page. */
          .cost-summary-table thead { display: table-header-group }
          .cost-summary-table tr { break-inside: avoid; page-break-inside: avoid }          /* Fix: this component's outer wrapper is position:fixed with
             overflow-y:auto (a full-screen modal/overlay). That means the
             real document height stays capped at one viewport's worth of
             content, and any content scrolled past the visible area is
             outside the browser's actual page-layout flow. window.print()
             then only paginates within that single-viewport-tall document,
             which is why printing could show only the currently visible
             page/section instead of the full multi-page report. Neutralizing
             position/overflow here (print only) lets the document's true
             height include all three .page sections, so print pagination
             covers the complete report every time. */
          .exec-report-modal-root { position: static !important; overflow: visible !important; height: auto !important; inset: auto !important; }
        }
        /* Cost Summary table corner rounding — done here (not via
           overflow:hidden on the wrapping div) because overflow:hidden on
           an ancestor of a table that spans multiple PRINTED pages can
           clip/misrender content on later pages in some print engines
           (observed: the TOTAL row's last column rendering outside the
           table's visual frame on the printed PDF). first-child/last-child
           corner rounding on the table itself achieves the same rounded
           look without relying on ancestor clipping. */
        .cost-summary-table thead tr:first-child th:first-child { border-top-left-radius: 12px }
        .cost-summary-table thead tr:first-child th:last-child { border-top-right-radius: 12px }
        .cost-summary-table tbody tr:last-child td:first-child { border-bottom-left-radius: 12px }
        .cost-summary-table tbody tr:last-child td:last-child { border-bottom-right-radius: 12px }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>

      {/* ── Toolbar ── */}
      <div className="no-print" style={{position:'sticky',top:0,zIndex:10,background:'#1A1A2E',padding:'12px 24px',display:'flex',alignItems:'center',gap:12}}>
        <button onClick={onClose} style={{padding:'8px 16px',background:'rgba(255,255,255,.1)',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:13}}>← Back</button>
        <span style={{color:'rgba(255,255,255,.5)',fontSize:13,flex:1}}>Executive Summary Report — {p?.name}</span>
        <select
          value={configPeriod}
          onChange={e => setConfigPeriod(e.target.value)}
          className="no-print"
          style={{padding:'7px 12px',background:'rgba(255,255,255,.1)',color:'#fff',border:'1px solid rgba(255,255,255,.2)',borderRadius:8,fontSize:13,fontFamily:'inherit'}}
        >
          {configPeriodOptions().map(o => <option key={o.value} value={o.value} style={{color:'#000'}}>{o.label}</option>)}
        </select>
        <button onClick={() => { if(projectId) fetchAll(projectId) }} style={{padding:'8px 16px',background:'rgba(255,255,255,.1)',color:'#fff',border:'1px solid rgba(255,255,255,.2)',borderRadius:8,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>↻ Refresh</button>
        {onOpenConfig && (
          <button onClick={onOpenConfig} className="no-print" style={{padding:'8px 16px',background:'rgba(255,255,255,.1)',color:'#fff',border:'1px solid rgba(255,255,255,.2)',borderRadius:8,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>⚙ Report Configuration</button>
        )}
        <button onClick={handlePrint} style={{padding:'8px 20px',background:'#1B3A6B',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600}}>🖨 Print / Save PDF</button>
      </div>

      {/* ── Data Source selector — same reportContext pattern as CostControl.jsx ── */}
      <div className="no-print" style={{ position:'sticky', top:57, zIndex:9, padding:'8px 24px', background: reportContext.source === 'release' ? '#78350F' : '#111827',
        borderBottom:'1px solid rgba(255,255,255,.1)', display:'flex', alignItems:'center', gap:16 }}>
        <span style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'rgba(255,255,255,.5)' }}>Data Source:</span>
        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, cursor:'pointer', color: reportContext.source==='live' ? '#fff' : 'rgba(255,255,255,.6)' }}>
          <input type="radio" checked={reportContext.source === 'live'}
            onChange={() => setReportContext({ source: 'live', releaseId: null })}/>
          Current (Live)
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, cursor:'pointer', color: reportContext.source==='release' ? '#fff' : 'rgba(255,255,255,.6)' }}>
          <input type="radio" checked={reportContext.source === 'release'}
            onChange={() => {
              const latest = availableReleases[0]
              setReportContext({ source: 'release', releaseId: latest ? latest.id : null })
            }}/>
          Project Forecast Release
        </label>
        {reportContext.source === 'release' && (
          availableReleases.length === 0 ? (
            <span style={{ fontSize:12, color:'rgba(255,255,255,.5)', fontStyle:'italic' }}>No Releases exist yet for this project.</span>
          ) : (
            <select value={reportContext.releaseId || ''} onChange={e => setReportContext({ source: 'release', releaseId: e.target.value })}
              style={{ fontSize:12.5, fontWeight:600, color:'#FDE68A', border:'1px solid rgba(253,230,138,.4)', background:'rgba(0,0,0,.2)', borderRadius:6, padding:'4px 10px' }}>
              {availableReleases.map(r => {
                const directLabel = r.direct_snapshot_id ? `Direct Rev.${r.direct_revision_number}` : 'No Direct'
                const indirectLabel = r.indirect_snapshot_id ? `Indirect Rev.${r.indirect_revision_number}` : 'No Indirect'
                return (
                  <option key={r.id} value={r.id} style={{ color:'#000' }}>
                    Release #{r.release_number}{r.is_current ? ' (Current)' : ''} — {new Date(r.forecast_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})} · {directLabel} · {indirectLabel}
                  </option>
                )
              })}
            </select>
          )
        )}
        {reportContext.source === 'release' && reportContext.releaseId && (
          <span style={{ fontSize:11, color:'#FDE68A', display:'flex', alignItems:'center', gap:4 }}>
            🔒 Read-only — approved historical state · Trend Indicators and Cash Position not applicable · This Period requires an Actual Reporting Period (From/To Month) set in Report Configuration
          </span>
        )}
      </div>

      <div id="exec-report" ref={printRef} style={{maxWidth:1050,margin:'24px auto',display:'flex',flexDirection:'column',gap:24,padding:'0 20px 60px'}}>

        {/* ══════════════════════════════════════════
            PAGE 1 — COVER
        ══════════════════════════════════════════ */}
        <div className="page" style={{background:'#0F1017',borderRadius:16,overflow:'hidden',position:'relative'}}>
          {/* Green accent bar */}
          <div style={{height:4,background:'linear-gradient(90deg,#1B3A6B,#5DADE2,#1B3A6B)'}}/>

          {/* Header */}
          <div style={{padding:'32px 40px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid rgba(255,255,255,.08)'}}>
            {/* Logo */}
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <ReportLogo org={org} size={44}/>
              <div>
                <div style={{color:'#fff',fontWeight:800,fontSize:18,letterSpacing:'.04em'}}>{org?.name || (<>Cost<span style={{color:'#5DADE2'}}>Space</span></>)}</div>
                <div style={{color:'rgba(255,255,255,.4)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>{org?.name ? 'Powered by CostSpace' : 'Enterprise Cost Intelligence'}</div>
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{color:'rgba(255,255,255,.3)',fontSize:10,letterSpacing:'.1em',textTransform:'uppercase'}}>Generated</div>
              <div style={{color:'rgba(255,255,255,.7)',fontSize:12,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{today()}</div>
            </div>
          </div>

          {/* Main cover content */}
          <div style={{padding:'48px 40px 40px',display:'flex',flexDirection:'column',gap:32}}>
            <div>
              {/* Batch B: report_title overrides the default label when set;
                  report_subtitle is an additional line shown only if present. */}
              <div style={{color:'#5DADE2',fontSize:11,fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',marginBottom:10}}>{config?.report_title || 'Executive Summary Report'}</div>
              <div style={{color:'#fff',fontSize:42,fontWeight:900,lineHeight:1.1,letterSpacing:'-.02em'}}>{p?.name || 'Project Name'}</div>
              <div style={{color:'rgba(255,255,255,.4)',fontSize:16,marginTop:8}}>{config?.report_subtitle || 'Cost Performance · Forecast · Gross Profit Analysis'}</div>
            </div>

            {/* Meta cards — Client/Main Contractor are omitted entirely when
                not yet set on the project, instead of showing a bare "—",
                which reads as a data-quality issue on the report's first page.
                Grid column count adapts to the actual item count (visible
                items are always a multiple of 2 or 3 in practice) so an
                omitted field never leaves a single orphaned card on its own row.
                Batch B: Prepared By / Approved By are added only when set in
                the Report Configuration. % Complete was replaced with Total
                Commitment (product decision, Data Consistency migration) —
                config.manual_percent_complete still exists as a field in
                ExecutiveReportConfig.jsx for potential future use, it's just
                not displayed on this page anymore. */}
            {(() => {
              const metaItems = [
                { label:'Reporting Period', value: config?.report_date ? new Date(config.report_date+'T00:00:00').toLocaleDateString('en-GB',{month:'long',year:'numeric'}) : new Date(configPeriod+'T00:00:00').toLocaleDateString('en-GB',{month:'long',year:'numeric'}) },
                // Data Source — shown on the printed page itself (not just
                // the on-screen toolbar), so a saved/printed PDF always
                // makes clear whether it reflects Current (Live) data or a
                // frozen historical Release, exactly as CostControl.jsx's
                // own PDF export already states its dataSourceLabel.
                d.isRelease && d.releaseInfo && { label:'Data Source', value: `Release #${d.releaseInfo.release_number}` },
                !d.isRelease && { label:'Data Source', value: 'Current (Live)' },
                p?.client_name       && { label:'Client',          value: p.client_name },
                p?.main_contractor   && { label:'Main Contractor', value: p.main_contractor },
                { label:'Contract Value',   value: `SAR ${fmtM(d.contractValue)}` },
                { label:'Project Status',   value: p?.status || 'Under Construction' },
                { label:'Total Commitment', value: `SAR ${fmtM(d.commitment)}` },
                config?.prepared_by  && { label:'Prepared By',  value: config.prepared_by },
                config?.approved_by  && { label:'Approved By',  value: config.approved_by },
              ].filter(Boolean)
              const cols = metaItems.length % 3 === 0 ? 3 : metaItems.length % 2 === 0 ? 2 : 3
              return (
                <div style={{display:'grid',gridTemplateColumns:`repeat(${cols},1fr)`,gap:16,marginTop:8}}>
                  {metaItems.map(item => (
                <div key={item.label} style={{background:'rgba(255,255,255,.05)',borderRadius:10,padding:'16px 20px',border:'1px solid rgba(255,255,255,.08)'}}>
                  <div style={{color:'rgba(255,255,255,.35)',fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>{item.label}</div>
                  <div style={{color:'#fff',fontSize:15,fontWeight:700,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{item.value}</div>
                </div>
                  ))}
                </div>
              )
            })()}

            {/* Confidential badge */}
            <div style={{marginTop:'auto',display:'flex',alignItems:'center',justifyContent:'space-between',paddingTop:24,borderTop:'1px solid rgba(255,255,255,.08)'}}>
              <div style={{color:'rgba(255,255,255,.25)',fontSize:11}}>🔒 Strictly Confidential · For Top Management Use Only</div>
              <div style={{color:'rgba(255,255,255,.25)',fontSize:11}}>Page 1</div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            PAGE 2 — EXECUTIVE BRIEF (Batch B)
            Manually-entered narrative content from Report Configuration.
            Rendered right after the Cover so executives read the narrative
            context before any financial data. Skipped entirely if none of
            its five fields are set — no blank page, no empty section
            headers, no placeholder text (same rule as before the move).
        ══════════════════════════════════════════ */}
        {hasBrief && (
          <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
            <div style={{background:'#0F1017',padding:'16px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center'}}>
                <ReportLogo org={org} size={28}/>
                <span style={{color:'#fff',fontWeight:700,fontSize:13,marginLeft:10}}>{org?.name || (<>Cost<span style={{color:'#5DADE2'}}>Space</span></>)}</span>
                <span style={{color:'rgba(255,255,255,.3)',fontSize:13,margin:'0 8px'}}>·</span>
                <span style={{color:'rgba(255,255,255,.6)',fontSize:13}}>{p?.name} — Executive Brief</span>
              </div>
              <div style={{color:'rgba(255,255,255,.4)',fontSize:11}}>{monthYear()} · Page {briefPageNum}</div>
            </div>

            <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:20}}>
              {briefSections.map(section => (
                <div key={section.label}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                    <div style={{width:4,height:18,background:'#1B3A6B',borderRadius:2}}/>
                    <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>{section.label}</span>
                  </div>
                  <div style={{fontSize:13,color:'#374151',lineHeight:1.6,whiteSpace:'pre-wrap',paddingLeft:12}}>{section.value}</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 32px',borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>{p?.name} · Executive Summary Report · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Strictly Confidential — Top Management Only · Page {briefPageNum}</div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            PAGE 3 — KPIs + GROSS PROFIT
        ══════════════════════════════════════════ */}
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          {/* Page header */}
          <div style={{background:'#0F1017',padding:'16px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <ReportLogo org={org} size={28}/>
              <span style={{color:'#fff',fontWeight:700,fontSize:13}}>{org?.name || (<>Cost<span style={{color:'#5DADE2'}}>Space</span></>)}</span>
              <span style={{color:'rgba(255,255,255,.3)',fontSize:13,margin:'0 8px'}}>·</span>
              <span style={{color:'rgba(255,255,255,.6)',fontSize:13}}>{p?.name} — Executive Summary</span>
            </div>
            <div style={{color:'rgba(255,255,255,.4)',fontSize:11}}>{monthYear()} · Page {kpiPageNum}</div>
          </div>

          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:20}}>
            {/* Section title */}
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:4,height:20,background:'#1B3A6B',borderRadius:2}}/>
              <span style={{fontWeight:800,fontSize:15,color:'#0F1017'}}>Project Key Performance Indicators</span>
              <span style={{color:'#9CA3AF',fontSize:12,marginLeft:'auto'}}>As of {today()}</span>
            </div>

            {/* KPI Row 1 */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12}}>
              {[
                { label:'Contract Value',     value:fmtM(d.contractValue), sub:'SAR · Selling Price',     bg:'#0F1017', color:'#fff', sub_c:'rgba(255,255,255,.4)' },
                { label:'Budget (BAC)',        value:fmtM(d.bac),           sub:'SAR · Direct + Indirect', bg:'#EFF6FF', color:'#1D4ED8', sub_c:'#93C5FD' },
                // "This Period" sub-label is Live-only (confirmed Out of
                // Scope for Release — see the Executive Report TODO list:
                // it's a period-over-period movement analysis, not part
                // of the Cost Forecast's frozen financial state). Falls
                // back to a neutral sub-label for Release instead of
                // showing "+0 This Period", which would misleadingly read
                // as a real (zero) value rather than an inapplicable one.
                { label:'Actual Cost to Date', value:fmtM(d.actualCost),   sub: (d.isRelease && !d.hasConfiguredActualPeriod) ? 'SAR · As of Release' : `+${fmtM(d.thisPeriod)} This Period`, bg:'#F0FDF4', color:'#1A7A4A', sub_c:'#6EE7B7' },
                { label:'Forecast (EAC)',      value:fmtM(d.eac),           sub:'Estimate at Completion',  bg:'#FFFBEB', color:'#D97706', sub_c:'#FCD34D', trend:d.trends?.eac },
                { label:'Cost Variance (VAC)', value:(d.vac<0?'-':'')+fmtM(Math.abs(d.vac)), sub: d.vac<0?'EAC over Budget':'Under Budget', bg: d.vac>=0?'#F0FDF4':'#FEF2F2', color: VAC_COLOR(d.vac), sub_c: d.vac>=0?'#86EFAC':'#FCA5A5', trend:d.trends?.vac },
              ].map(k => (
                <div key={k.label} style={{background:k.bg,borderRadius:12,padding:'16px',border:'1px solid rgba(0,0,0,.06)'}}>
                  <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',color:k.sub_c||k.color,opacity:.8,marginBottom:6}}>{k.label}</div>
                  <div style={{fontSize:20,fontWeight:800,color:k.color,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace",letterSpacing:'-.02em',display:'flex',alignItems:'baseline'}}>{k.value}<TrendBadge trend={k.trend}/></div>
                  <div style={{fontSize:10,color:k.sub_c||k.color,marginTop:4,opacity:.7}}>{k.sub}{k.trend && k.trend.previous!==0 ? ' · vs last month' : ''}</div>
                </div>
              ))}
            </div>

            {/* KPI Row 2 — Cash In/Out are confirmed Out of Scope for
                Release (not part of the Cost Forecast's frozen financial
                state — see the Executive Report TODO list for Cash
                Position). Rather than show fabricated zeros, this row
                drops to 2 cards (CPI, Total Commitment) when viewing a
                Release, instead of silently rendering "SAR 0" for Cash
                In/Out as if that were a real historical figure. */}
            <div style={{display:'grid',gridTemplateColumns: d.isRelease ? 'repeat(2,1fr)' : 'repeat(4,1fr)',gap:12}}>
              {[
                !d.isRelease && { label:'Cash In (Collected)', value:fmtM(d.cashIn),  sub:'From Client',    color:'#1A7A4A', bg:'#F0FDF4' },
                !d.isRelease && { label:'Cash Out (Paid)',      value:fmtM(d.cashOut), sub:'To Vendors',     color:'#DC2626', bg:'#FEF2F2' },
                { label:'CPI',                 value:d.cpiVal.toFixed(3), sub:'Cost Performance Index', color: d.cpiVal>=1?'#1A7A4A':'#D97706', bg: d.cpiVal>=1?'#F0FDF4':'#FFFBEB', trend:d.trends?.cpi },
                { label:'Total Commitment',    value:fmtM(d.commitment), sub:'Committed to Date', color:'#1D4ED8', bg:'#EFF6FF' },
              ].filter(Boolean).map(k => (
                <div key={k.label} style={{background:k.bg,borderRadius:12,padding:'16px',border:'1px solid rgba(0,0,0,.06)'}}>
                  <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',color:k.color,opacity:.8,marginBottom:6}}>{k.label}</div>
                  <div style={{fontSize:22,fontWeight:800,color:k.color,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace",display:'flex',alignItems:'baseline'}}>{k.value}{k.trend && <TrendBadge trend={k.trend} formatDiff={(n)=>n.toFixed(3)}/>}</div>
                  <div style={{fontSize:10,color:k.color,marginTop:4,opacity:.7}}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Gross Profit Analysis */}
            <div style={{background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB',padding:'20px 24px'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                <div style={{width:3,height:16,background:'#1B3A6B',borderRadius:2}}/>
                <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>Gross Profit Analysis — Contract Value vs Forecast at Completion</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {[
                    { label:'Contract Value (Selling Price)', value:`SAR ${fmt(d.contractValue)}`, bold:false },
                    { label:'Forecast at Completion (EAC)',   value:`SAR ${fmt(d.eac)}`,           bold:false },
                    { label:'Budget (BAC) for reference',     value:`SAR ${fmt(d.bac)}`,           bold:false },
                    { label:'Gross Profit (Loss) = CV − EAC', value:(d.grossProfit<0?'-':'')+'SAR '+fmt(Math.abs(d.grossProfit)), bold:true, color: d.grossProfit>=0?'#1A7A4A':'#DC2626' },
                  ].map(row => (
                    <div key={row.label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:'#fff',borderRadius:8,border:'1px solid #E5E7EB'}}>
                      <span style={{fontSize:12,color:'#6B7280'}}>{row.label}</span>
                      <span style={{fontSize:13,fontWeight:row.bold?800:600,color:row.color||'#0F1017',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#6B7280',marginBottom:4}}>Profit Trend — Budget vs Forecast</div>
                  {[
                    { label:'Budget Gross Profit', value: d.budgetProfit, pctVal: d.contractValue ? (d.budgetProfit/d.contractValue*100).toFixed(1) : 0 },
                    { label:'Forecast Gross Profit', value: d.grossProfit, pctVal: d.contractValue ? (d.grossProfit/d.contractValue*100).toFixed(1) : 0 },
                  ].map(row => (
                    <div key={row.label} style={{padding:'12px 16px',background: row.value>=0?'#F0FDF4':'#FEF2F2',borderRadius:10,border:`1px solid ${row.value>=0?'#BBF7D0':'#FECACA'}`}}>
                      <div style={{fontSize:11,color: row.value>=0?'#166534':'#991B1B',marginBottom:4}}>{row.label}</div>
                      <div style={{fontSize:18,fontWeight:800,color: row.value>=0?'#1A7A4A':'#DC2626',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace",display:'flex',alignItems:'baseline'}}>
                        {row.value>=0?'+':'-'}SAR {fmtM(Math.abs(row.value))} ({row.value>=0?'+':''}{row.pctVal}%)
                        {row.label === 'Forecast Gross Profit' && <TrendBadge trend={d.trends?.grossProfit}/>}
                      </div>
                    </div>
                  ))}
                  {d.grossProfit < 0 && (
                    <div style={{padding:'10px 14px',background:'#FFFBEB',borderRadius:8,border:'1px solid #FDE68A',fontSize:11,color:'#92400E',marginTop:4}}>
                      ⚠ Project is currently forecasting a loss of SAR {fmtM(Math.abs(d.grossProfit))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Trend Narrative — plain-language translation of d.trends into
                executive sentences. No comparison/calculation happens here;
                narrateMonetaryTrend()/narrateCpiTrend() (trendCompare.js) only
                format fields that already exist on the trend objects. */}
            {(() => {
              const lines = [
                narrateMonetaryTrend(d.trends?.eac, { metric: 'Forecast (EAC)', up: 'increased', down: 'decreased' }, fmtM),
                narrateMonetaryTrend(d.trends?.grossProfit, { metric: 'Forecast Gross Profit', up: 'improved', down: 'declined' }, fmtM),
                narrateCpiTrend(d.trends?.cpi),
              ].filter(Boolean)
              if (!lines.length) return null
              return (
                <div style={{background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB',padding:'16px 20px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                    <div style={{width:3,height:14,background:'#1B3A6B',borderRadius:2}}/>
                    <span style={{fontWeight:700,fontSize:12,color:'#0F1017'}}>Month-over-Month Trend Summary</span>
                  </div>
                  <ul style={{margin:0,paddingLeft:18,display:'flex',flexDirection:'column',gap:6}}>
                    {lines.map((line, i) => (
                      <li key={i} style={{fontSize:12,color:'#374151',lineHeight:1.5}}>{line}</li>
                    ))}
                  </ul>
                </div>
              )
            })()}

            {/* Footer */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>{p?.name} · Executive Summary Report · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Strictly Confidential — Top Management Only · Page {kpiPageNum}</div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            PAGE {decisionsPageNum} — EXECUTIVE DECISIONS
            Areas of Concern + Top Risks + Top Cost Savings + Cash Position —
            everything requiring management attention in one place, so an
            executive can open a single page and understand what's going
            wrong, the main risks, what's going well, and the current cash
            position.
            NOTE: an earlier version of this page also had a separate "Top
            Cost Overruns" section, but it used the exact same filter/sort
            as Areas of Concern (vac < 0, sorted ascending) — a duplicate
            presentation of the same business concept, not a distinct one.
            It was removed; Areas of Concern now covers that role at a size
            (10 items) suited to this page. Top Cost Savings remains because
            it's genuinely distinct (the opposite VAC direction, vac > 0).
            Ranking is based on current VAC values only for now (per
            explicit product decision — more advanced prioritization can be
            introduced later).
        ══════════════════════════════════════════ */}
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          {/* Page header */}
          <div style={{background:'#0F1017',padding:'16px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <ReportLogo org={org} size={28}/>
              <span style={{color:'#fff',fontWeight:700,fontSize:13}}>{org?.name || (<>Cost<span style={{color:'#5DADE2'}}>Space</span></>)}</span>
              <span style={{color:'rgba(255,255,255,.3)',fontSize:13,margin:'0 8px'}}>·</span>
              <span style={{color:'rgba(255,255,255,.6)',fontSize:13}}>{p?.name} — Executive Decisions</span>
            </div>
            <div style={{color:'rgba(255,255,255,.4)',fontSize:11}}>{monthYear()} · Page {decisionsPageNum}</div>
          </div>

          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:20}}>
            {/* Report Confidence — communicates how much confidence to place
                in this report's figures based on completeness/consistency
                of the underlying data. Not a KPI, not a judgment on project
                performance — purely a data-reliability signal, shown before
                the executive insights below so readers calibrate their
                interpretation first. Two compact tiers only: a one-line
                summary, then a short bullet list of detected issues (if any)
                — no long explanations, no repeated information. */}
            {(() => {
              const dc = d.dataConfidence
              const statusMeta = {
                full:     { icon:'🟢', label:'Full Report Confidence',     bg:'#F0FDF4', border:'#BBF7D0', color:'#1A7A4A' },
                moderate: { icon:'🟡', label:'Moderate Report Confidence', bg:'#FFFBEB', border:'#FDE68A', color:'#92400E' },
                low:      { icon:'🔴', label:'Low Report Confidence',      bg:'#FFF8F8', border:'#FECACA', color:'#991B1B' },
              }[dc.overallStatus]
              return (
                <div style={{background:statusMeta.bg,border:`1px solid ${statusMeta.border}`,borderRadius:12,padding:'14px 20px'}}>
                  {/* Tier 1 — one-line executive summary */}
                  <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                    <span style={{fontWeight:800,fontSize:13,color:statusMeta.color}}>{statusMeta.icon} {statusMeta.label}</span>
                    <span style={{fontSize:11,color:'#6B7280'}}>Critical Issues: <b style={{color:'#0F1017'}}>{dc.critical.length}</b></span>
                    <span style={{fontSize:11,color:'#6B7280'}}>Warnings: <b style={{color:'#0F1017'}}>{dc.warnings.length}</b></span>
                    <span style={{fontSize:11,color:'#6B7280'}}>Forecast Coverage: <b style={{color:'#0F1017'}}>{dc.forecastCoverage.withForecast} / {dc.forecastCoverage.total} packages ({dc.forecastCoverage.pct.toFixed(0)}%)</b></span>
                  </div>
                  {/* Tier 2 — short bullet list, issues only, no elaboration */}
                  {!dc.isFullyConfident && (
                    <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${statusMeta.border}`,display:'flex',flexDirection:'column',gap:3}}>
                      {dc.critical.map((item, i) => (
                        <div key={`c${i}`} style={{fontSize:11,color:'#991B1B'}}>• {item.message}</div>
                      ))}
                      {dc.warnings.map((item, i) => (
                        <div key={`w${i}`} style={{fontSize:11,color:'#92400E'}}>• {item.message}</div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Top row: Areas of Concern + Top Cost Overruns */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {/* Areas of Concern */}
              <div style={{background:'#FFF8F8',border:'1px solid #FECACA',borderRadius:12,padding:'16px 20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                  <div style={{width:3,height:16,background:'#DC2626',borderRadius:2}}/>
                  <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>⚠️ Areas of Concern</span>
                </div>
                {d.concerns.length === 0 ? (
                  <div style={{color:'#1A7A4A',fontSize:13,fontWeight:600}}>✅ All packages within budget</div>
                ) : d.concerns.map((pk,i) => (
                  <div key={pk.name} style={{marginBottom:10,paddingBottom:10,borderBottom: i<d.concerns.length-1?'1px solid #FEE2E2':'none'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                      <span style={{fontSize:13}}>🔴</span>
                      <span style={{fontWeight:700,fontSize:12,color:'#0F1017'}}>{pk.name}</span>
                      <span style={{marginLeft:'auto',fontWeight:800,color:'#DC2626',fontSize:12,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>-SAR {fmtM(Math.abs(pk.vac))}</span>
                    </div>
                    <div style={{fontSize:11,color:'#6B7280',paddingLeft:20}}>
                      EAC {fmtM(pk.eac ?? 0)} vs Budget {fmtM(pk.budget)} · CPI {pk.cpi?pk.cpi.toFixed(2):'—'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Risk Register — ranked by real financial exposure
                  (financial_impact), not the theoretical Probability x
                  Impact score alone. Shows risk_score alongside
                  residual_score (post-mitigation) so an executive can see
                  whether mitigation is actually reducing exposure, plus the
                  owner accountable for it. All fields come from the same
                  risk_register row already fetched above — no new query,
                  no new calculation, only a change in sort order and which
                  existing fields are displayed. */}
              <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:12,padding:'16px 20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                  <div style={{width:3,height:16,background:'#D97706',borderRadius:2}}/>
                  <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>🚩 Top Risks — by Financial Exposure{d.isRelease ? ' (Current, Live)' : ''}</span>
                </div>
                {d.risks.length === 0 ? (
                  <div style={{color:'#1A7A4A',fontSize:13,fontWeight:600}}>✅ No open risks recorded</div>
                ) : d.risks.map((risk, i) => (
                  <div key={risk.id || i} style={{marginBottom:10,paddingBottom:10,borderBottom: i<d.risks.length-1?'1px solid #FDE68A':'none'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                      <span style={{fontSize:13}}>🟠</span>
                      <span style={{fontWeight:700,fontSize:12,color:'#0F1017'}}>{risk.risk_description || 'Untitled Risk'}</span>
                      <span style={{marginLeft:'auto',fontWeight:800,color:'#D97706',fontSize:12,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>
                        {risk.financial_impact ? `SAR ${fmt(risk.financial_impact)}` : 'No $ estimate'}
                      </span>
                    </div>
                    <div style={{fontSize:11,color:'#6B7280',paddingLeft:20,display:'flex',alignItems:'center',gap:10}}>
                      {risk.risk_score != null && (
                        <span>Score {risk.risk_score}{risk.residual_score != null ? ` → ${risk.residual_score} residual` : ''}</span>
                      )}
                      {risk.owner && <span>· Owner: {risk.owner}</span>}
                    </div>
                    {risk.mitigation && (
                      <div style={{fontSize:11,color:'#6B7280',paddingLeft:20,marginTop:2}}>↳ {risk.mitigation}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Second row: Top Cost Savings + Cash Position */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {/* Top Cost Savings — best VAC, current values only */}
              <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'16px 20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                  <div style={{width:3,height:16,background:'#1A7A4A',borderRadius:2}}/>
                  <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>📈 Top Cost Savings</span>
                </div>
                {d.topSavings.length === 0 ? (
                  <div style={{color:'#6B7280',fontSize:13}}>No savings identified</div>
                ) : d.topSavings.map((pk,i) => (
                  <div key={pk.name} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom: i<d.topSavings.length-1?'1px solid #F3F4F6':'none'}}>
                    <span style={{fontSize:12,color:'#374151',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pk.name}</span>
                    <span style={{fontSize:12,fontWeight:700,color:'#1A7A4A',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>+SAR {fmtM(pk.vac)}</span>
                  </div>
                ))}
              </div>

              {/* Cash Position — confirmed Out of Scope for Release (not
                  part of the Cost Forecast's frozen financial state — see
                  the Executive Report TODO list). Shows an explicit
                  "Not Applicable" message for Release instead of
                  rendering SAR 0 / SAR 0 / +SAR 0 as if that were the
                  real historical cash position. */}
              <div style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:12,padding:'16px 20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                  <div style={{width:3,height:16,background:'#1B3A6B',borderRadius:2}}/>
                  <span style={{fontWeight:700,fontSize:13,color:'#0F1017'}}>💵 Cash Position</span>
                </div>
                {d.isRelease ? (
                  <div style={{fontSize:12,color:'#9CA3AF',fontStyle:'italic',padding:'12px 0'}}>Not applicable for Release Snapshot — Cash Position reflects the project's current live financial status, not this Release's frozen forecast state.</div>
                ) : (
                  <>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {[
                        { label:'Cash In — Collected',   value: fmt(d.cashIn),  color:'#1A7A4A' },
                        { label:'Cash Out — Paid',        value: fmt(d.cashOut), color:'#DC2626' },
                        { label:'Net Cash Position',      value: (d.netCash<0?'-':'+')+' SAR '+fmtM(Math.abs(d.netCash)), color: d.netCash>=0?'#1A7A4A':'#DC2626', bold:true },
                      ].map(row => (
                        <div key={row.label} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #E5E7EB'}}>
                          <span style={{fontSize:12,color:'#6B7280'}}>{row.label}</span>
                          <span style={{fontSize:13,fontWeight:row.bold?800:600,color:row.color,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{row.value}</span>
                        </div>
                      ))}
                    </div>
                    {d.netCash < 0 ? (
                      <div style={{marginTop:12,padding:'10px 12px',background:'#FFFBEB',borderRadius:8,fontSize:11,color:'#92400E',border:'1px solid #FDE68A'}}>
                        ⚠ Negative cash position — Management action required
                      </div>
                    ) : (
                      <div style={{marginTop:12,padding:'10px 12px',background:'#F0FDF4',borderRadius:8,fontSize:11,color:'#166534',border:'1px solid #BBF7D0'}}>
                        ✅ Cash collected currently exceeds cash paid — no funding gap at this time
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>{p?.name} · Executive Decisions · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Strictly Confidential — Top Management Only · Page {decisionsPageNum}</div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            APPENDIX — FULL COST SUMMARY (all Cost Codes)
            The complete detailed table, moved here so it no longer
            interrupts the executive narrative. Same data, same columns,
            same Direct/Indirect grouping and Total row as before.
        ══════════════════════════════════════════ */}
        <div className="page page-break-before" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          {/* Page header */}
          <div style={{background:'#0F1017',padding:'16px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <ReportLogo org={org} size={28}/>
              <span style={{color:'#fff',fontWeight:700,fontSize:13}}>{org?.name || (<>Cost<span style={{color:'#5DADE2'}}>Space</span></>)}</span>
              <span style={{color:'rgba(255,255,255,.3)',fontSize:13,margin:'0 8px'}}>·</span>
              <span style={{color:'rgba(255,255,255,.6)',fontSize:13}}>{p?.name} — Appendix: Full Cost Summary</span>
            </div>
            <div style={{color:'rgba(255,255,255,.4)',fontSize:11}}>{monthYear()} · Page {appendixPageNum}</div>
          </div>

          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:20}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
                <div style={{width:4,height:18,background:'#1B3A6B',borderRadius:2}}/>
                <span style={{fontWeight:800,fontSize:14,color:'#0F1017'}}>Cost Summary — All Packages</span>
                <span style={{color:'#9CA3AF',fontSize:11,marginLeft:'auto'}}>SAR · {monthYear()}</span>
              </div>
              <div style={{borderRadius:12,border:'1px solid #E5E7EB'}}>
                <table className="cost-summary-table" style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:12}}>
                  <thead>
                    <tr style={{background:'#0F1017'}}>
                      {/* "This Period" column is confirmed Out of Scope
                          for Release (period-over-period movement, not
                          part of the frozen Cost Forecast financial
                          state) — dropped from the table entirely for
                          Release rather than showing a column of
                          fabricated zeros. */}
                      {['Package','Budget (BAC)','Actual Cost', (!d.isRelease || d.hasConfiguredActualPeriod) && 'This Period','EAC','VAC','CPI'].filter(Boolean).map(h => (
                        <th key={h} style={{padding:'10px 12px',color:'rgba(255,255,255,.7)',fontWeight:600,fontSize:10,textTransform:'uppercase',letterSpacing:'.06em',textAlign: h==='Package'?'left':'right'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Direct header */}
                    <tr style={{background:'#F3F4F6'}}>
                      <td colSpan={(!d.isRelease || d.hasConfiguredActualPeriod)?7:6} style={{padding:'6px 12px',fontSize:11,fontWeight:700,color:'#6B7280',letterSpacing:'.06em'}}>▸ DIRECT WORKS</td>
                    </tr>
                    {d.packages.filter(pk=>pk.type==='direct').map((pk,i) => (
                      <tr key={pk.name} style={{background: i%2===0?'#fff':'#F9FAFB',borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'9px 12px',fontWeight:500,color:'#0F1017',maxWidth:180}}>{pk.name}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.budget)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.actual)}</td>
                        {(!d.isRelease || d.hasConfiguredActualPeriod) && <td style={{padding:'9px 12px',textAlign:'right',color:'#6B7280',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace",fontSize:11}}>{fmt(pk.thisPeriod||0)}</td>}
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.eac ?? 0)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:VAC_COLOR(pk.vac),fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{pk.vac>=0?'+':''}{fmt(pk.vac)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color: pk.cpi>=1?'#1A7A4A':pk.cpi>=0.8?'#D97706':'#DC2626',fontWeight:600,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{pk.cpi?pk.cpi.toFixed(2):'—'}</td>
                      </tr>
                    ))}
                    {/* Indirect header */}
                    <tr style={{background:'#F3F4F6'}}>
                      <td colSpan={(!d.isRelease || d.hasConfiguredActualPeriod)?7:6} style={{padding:'6px 12px',fontSize:11,fontWeight:700,color:'#6B7280',letterSpacing:'.06em'}}>▸ INDIRECT WORKS</td>
                    </tr>
                    {d.packages.filter(pk=>pk.type==='indirect').map((pk,i) => (
                      <tr key={pk.name} style={{background: i%2===0?'#fff':'#F9FAFB',borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'9px 12px',fontWeight:500,color:'#0F1017',maxWidth:180}}>{pk.name}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.budget)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.actual)}</td>
                        {(!d.isRelease || d.hasConfiguredActualPeriod) && <td style={{padding:'9px 12px',textAlign:'right',color:'#6B7280',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace",fontSize:11}}>{fmt(pk.thisPeriod||0)}</td>}
                        <td style={{padding:'9px 12px',textAlign:'right',color:'#374151',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(pk.eac ?? 0)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:VAC_COLOR(pk.vac),fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{pk.vac>=0?'+':''}{fmt(pk.vac)}</td>
                        <td style={{padding:'9px 12px',textAlign:'right',color: pk.cpi>=1?'#1A7A4A':pk.cpi>=0.8?'#D97706':'#DC2626',fontWeight:600,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{pk.cpi?pk.cpi.toFixed(2):'—'}</td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr style={{background:'#0F1017'}}>
                      <td style={{padding:'11px 12px',color:'#fff',fontWeight:700}}>TOTAL</td>
                      <td style={{padding:'11px 12px',textAlign:'right',color:'#fff',fontWeight:700,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(d.bac)}</td>
                      <td style={{padding:'11px 12px',textAlign:'right',color:'#fff',fontWeight:700,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(d.actualCost)}</td>
                      <td style={{padding:'11px 12px',textAlign:'right',color:'rgba(255,255,255,.6)',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(d.thisPeriod)}</td>
                      <td style={{padding:'11px 12px',textAlign:'right',color:'#fff',fontWeight:700,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{fmt(d.eac)}</td>
                      <td style={{padding:'11px 12px',textAlign:'right',fontWeight:800,color: d.vac>=0?'#6EE7B7':'#FCA5A5',fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{d.vac>=0?'+':''}{fmt(d.vac)}</td>
                      <td style={{padding:'11px 12px',textAlign:'right',color:'#fff',fontWeight:700,fontFamily:"'JetBrains Mono','SF Mono','Consolas',monospace"}}>{d.cpiVal.toFixed(3)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>{p?.name} · Appendix — Full Cost Summary · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Strictly Confidential — Top Management Only · Page {appendixPageNum}</div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            LAST PAGE — EXECUTIVE BRIEF (Batch B) moved to right after Cover
        ══════════════════════════════════════════ */}

      </div>
    </div>
  )
}
